import type { FilterOption } from '@/scopes/search/types'
import { space } from '@sunwebgroup/ui-components'
import { Accordion } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useMultiSelectFilter } from '@/scopes/search/search/domain/hooks'
import { ArrivalAirportFilter } from '@/scopes/search/search/pages/SearchPage/components/Filters/AirportFilter'
import { useSearchDependencies } from '@/scopes/search/search/pages/SearchPage/context'
import { createSearchFilterClickEvent } from '../../../../domain/analytics'
import { FilterGroupProps } from '../types'
import { DropdownMultiselect, useEditableDropdownMultiselect } from './DropdownMultiselect'
import { FormField, FormFieldControl, FormFieldLabel } from './FormField'

const FocusPadding = styled.div`
  padding: ${space('primitives_space_12')};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: ${space('primitives_space_16')};

    &:last-child {
      margin-bottom: 0;
    }
  }
`

function CountryFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('Country')
  const { destinationFilterActions } = useSearchDependencies()

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    destinationFilterActions.setCountryFilterSelection(newSelectedValues)
  }, [destinationFilterActions])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'country',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_country_label' })}</FormFieldLabel>
      <FormFieldControl data-qa="country-filter">
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_country_placeholder' })}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection data-qa="country-filter-selection">
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover title={intl.formatMessage({ id: 'destination_filter_country_label' })} closeText={intl.formatMessage({ id: 'close' })}>
            <FocusPadding>
              {options.map(({ value, name, resultsCount }) => (
                <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount} data-qa="country-filter-option">
                  {name}
                </DropdownMultiselect.CheckboxWithCounterOption>
              ))}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

function RegionFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('Region')
  const { destinationFilterActions } = useSearchDependencies()

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    destinationFilterActions.setRegionFilterSelection(newSelectedValues)
  }, [destinationFilterActions])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'region',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_region_label' })}</FormFieldLabel>
      <FormFieldControl data-qa="region-filter">
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_region_placeholder' })}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection data-qa="region-filter-selection">
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover title={intl.formatMessage({ id: 'destination_filter_region_label' })} closeText={intl.formatMessage({ id: 'close' })}>
            <FocusPadding>
              {options.map(({ value, name, resultsCount }) => (
                <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount} data-qa="region-filter-option">
                  {name}
                </DropdownMultiselect.CheckboxWithCounterOption>
              ))}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

function SubregionFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('Subregion')
  const { destinationFilterActions } = useSearchDependencies()

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    destinationFilterActions.setSubregionFilterSelection(newSelectedValues)
  }, [destinationFilterActions])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'subregion',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_subregion_label' })}</FormFieldLabel>
      <FormFieldControl data-qa="subregion-filter">
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_subregion_placeholder' })}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection data-qa="subregion-filter-selection">
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover title={intl.formatMessage({ id: 'destination_filter_subregion_label' })} closeText={intl.formatMessage({ id: 'close' })}>
            <FocusPadding>
              {options.map(({ value, name, resultsCount }) => (
                <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount} data-qa="subregion-filter-option">
                  {name}
                </DropdownMultiselect.CheckboxWithCounterOption>
              ))}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

function CityFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('City')
  const { destinationFilterActions } = useSearchDependencies()

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    destinationFilterActions.setCityFilterSelection(newSelectedValues)
  }, [destinationFilterActions])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'city',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_city_label' })}</FormFieldLabel>
      <FormFieldControl data-qa="city-filter">
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_city_placeholder' })}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection data-qa="city-filter-selection">
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover title={intl.formatMessage({ id: 'destination_filter_city_label' })} closeText={intl.formatMessage({ id: 'close' })}>
            <FocusPadding>
              {options.map(({ value, name, resultsCount }) => (
                <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount} data-qa="city-filter-option">
                  {name}
                </DropdownMultiselect.CheckboxWithCounterOption>
              ))}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

export function DestinationFilter({ defaultOpen }: FilterGroupProps) {
  const intl = useIntl()
  const { options: countries } = useMultiSelectFilter('Country')
  const { options: regions } = useMultiSelectFilter('Region')
  const { options: subRegions } = useMultiSelectFilter('Subregion')
  const { options: cities } = useMultiSelectFilter('City')
  const { options: arrivals } = useMultiSelectFilter('ArrivalAirport')

  const showWrapper = !isEmpty(countries) || !isEmpty(regions) || !isEmpty(subRegions) || !isEmpty(cities) || !isEmpty(arrivals)

  return (
    showWrapper && (
      <Accordion.Item title={intl.formatMessage({ id: 'destination_filter_label' })} defaultOpen={defaultOpen}>
        <Wrapper>
          <CountryFilter />
          <RegionFilter />
          <SubregionFilter />
          <CityFilter />
          <ArrivalAirportFilter />
        </Wrapper>
      </Accordion.Item>
    )
  )
}

function mapLocalSelectionToGA4Result(localSelection: string[], options: FilterOption<string>[]) {
  return options
    .filter(option => localSelection.includes(option.value))
    .map(option => option.name)
    .join(',')
}
